import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { registerUser } from "../api/register";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from 'react-google-recaptcha';

const validationSchema = Yup.object({
  fullName: Yup.string().required("Full Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  contactNumber: Yup.string()
    .matches(/^\d*$/, "Contact Number must contain only digits"),
  companyName: Yup.string(),
  companyAddress: Yup.string(),
  cityName: Yup.string(),
  demoSelection: Yup.string(),
});

const RegistrationForm = () => {
  const [loading, setLoading] = useState(false);
  const [isDemoRequest, setIsDemoRequest] = useState(false);
  const navigate = useNavigate();
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  // Check if this is a demo request
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isDemo = urlParams.get('demo');
    
    if (isDemo === 'true') {
      setIsDemoRequest(true);
      
      // Get demo product info from localStorage
      const demoData = localStorage.getItem('demoRequest');
      if (demoData) {
        try {
          const parsedData = JSON.parse(demoData);
          // setDemoProduct(parsedData.product); // This line was removed
        } catch (error) {
          console.error('Error parsing demo data:', error);
        }
      }
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      contactNumber: "",
      companyName: "",
      companyAddress: "",
      cityName: "",
      demoSelection: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);
      try {
        if (!recaptchaToken) {
          toast.error('Please complete the reCAPTCHA.', { position: 'top-right', style: { zIndex: 9999 } });
          setLoading(false);
          return;
        }
        // Send form data + token to backend
        await registerUser({ ...values, recaptchaToken });
        toast.success("Registered successfully!", {
          position: "top-right",
          style: { zIndex: 9999 },
        });
        resetForm();
        setTimeout(() => {
          navigate("/registration-success", {
            state: {
              fullname: values.fullName,
              companyName: values.companyName,
            },
          });
        }, 1000); // Give user a moment to see the toast
      } catch (error) {
        toast.error("Registration failed: " + (error.message || "Unknown error"), {
          position: "top-right",
          style: { zIndex: 9999 },
        });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <ToastContainer position="top-right" style={{ zIndex: 9999 }} />
      <div className="registration-card-enhanced">
        <div className="registration-title-section">
          <h2 className="registration-title">Register Your Company</h2>
          <p className="registration-subtitle">Fill in the details below to get started or request a demo.</p>
        </div>
        <form className="registration-form" onSubmit={formik.handleSubmit}>
          <div className="registration-form-grid">
            <div className="input-icon-group">
              <i className="fa fa-user input-icon" />
              <input
                type="text"
                name="fullName"
                placeholder="Full Name"
                className="registration-input with-icon"
                {...formik.getFieldProps("fullName")}
              />
            </div>
            {formik.touched.fullName && formik.errors.fullName && (
              <div className="form-error">{formik.errors.fullName}</div>
            )}
            <div className="input-icon-group">
              <i className="fa fa-envelope input-icon" />
              <input
                type="email"
                name="email"
                placeholder="Valid Email"
                className="registration-input with-icon"
                {...formik.getFieldProps("email")}
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <div className="form-error">{formik.errors.email}</div>
            )}
            <div className="input-icon-group">
              <i className="fa fa-phone input-icon" />
              <input
                type="text"
                name="contactNumber"
                placeholder="Contact Number"
                className="registration-input with-icon"
                {...formik.getFieldProps("contactNumber")}
              />
            </div>
            {formik.touched.contactNumber && formik.errors.contactNumber && (
              <div className="form-error">{formik.errors.contactNumber}</div>
            )}
            <div className="input-icon-group">
              <i className="fa fa-building input-icon" />
              <input
                type="text"
                name="companyName"
                placeholder="Company Name"
                className="registration-input with-icon"
                {...formik.getFieldProps("companyName")}
              />
            </div>
            {formik.touched.companyName && formik.errors.companyName && (
              <div className="form-error">{formik.errors.companyName}</div>
            )}
            <div className="full-width input-icon-group">
              <i className="fa fa-map-marker input-icon" />
              <input
                type="text"
                name="companyAddress"
                placeholder="Company Address"
                className="registration-input with-icon"
                {...formik.getFieldProps("companyAddress")}
              />
              {formik.touched.companyAddress && formik.errors.companyAddress && (
                <div className="form-error">{formik.errors.companyAddress}</div>
              )}
            </div>
            <div className="input-icon-group">
              <i className="fa fa-location-arrow input-icon" />
              <input
                type="text"
                name="cityName"
                placeholder="City Name"
                className="registration-input with-icon"
                {...formik.getFieldProps("cityName")}
              />
              {formik.touched.cityName && formik.errors.cityName && (
                <div className="form-error">{formik.errors.cityName}</div>
              )}
            </div>
            <div className="input-icon-group">
              <i className="fa fa-list-alt input-icon" />
              <select
                name="demoSelection"
                className="registration-select with-icon"
                {...formik.getFieldProps("demoSelection")}
              >
                <option value="">Select Demo</option>
                <option value="HR Management Portal">HR Management Portal</option>
                <option value="Travel Agency Accounting Software">
                  Travel Agency Accounting Software
                </option>
                <option value="Inventory Management Software">
                  Inventory Management Software
                </option>
                <option value="E-commerce Software">E-commerce Software</option>
                <option value="General Accounting Software">
                  General Accounting Software
                </option>
              </select>
              {formik.touched.demoSelection && formik.errors.demoSelection && (
                <div className="form-error">{formik.errors.demoSelection}</div>
              )}
            </div>
          </div>
          {isDemoRequest && (
            <div className="demo-notice" style={{ marginBottom: '20px' }}>
              <i className="fa fa-info-circle"></i>
              <span>Demo requests are typically responded to within 24 hours during business days.</span>
            </div>
          )}
          <div style={{ margin: '20px 0' }}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={token => setRecaptchaToken(token)}
            />
          </div>
          <div className="registration-form-btn-row">
            <button type="submit" className="registration-form-btn enhanced-btn" disabled={loading}>
              {loading ? "Submitting..." : (isDemoRequest ? "Request Demo" : "Register Now")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default RegistrationForm;
